import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import { mfaSubmit } from "../redux/actionCreator";
import { saveSession } from "../utils/session";

/**
 * Two modes, decided by the server response:
 *
 * 1. `setup=true` — first-time enrolment.
 *    The server has stashed a pending secret. We render the otpauth QR plus
 *    the base32 string (so the user can key it in manually if the camera
 *    can't read the code). They scan/key, type the 6-digit code, hit Verify.
 *    On success we get back 8 backup codes — show them ONCE.
 *
 * 2. `setup=false` — routine second-factor prompt.
 *    Plain 6-digit input. There's also a "Use backup code" link that swaps
 *    the input to accept a 10-char alphanumeric backup code instead.
 *
 * Props:
 *   challenge = { mfaToken, mfaSetup, otpauthUrl?, secret? }
 *   onCancel  () => void              — back to login form
 *   onSuccess (body) => void           — body has accessToken/role/email
 */
export default function MfaChallenge({ challenge, onCancel, onSuccess }) {
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null); // only after setup
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [useBackup]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const payload = useBackup
        ? { mfaToken: challenge.mfaToken, backup: code.trim() }
        : { mfaToken: challenge.mfaToken, code: code.trim() };
      const res = await mfaSubmit(payload);
      const body = res?.data;
      if (!body?.status) {
        setErr(body?.message || "Invalid code");
        setBusy(false);
        return;
      }
      // Persist session (cookie was set server-side).
      saveSession({
        email: body.data?.email,
        role: body.data?.role,
      });
      if (body.mfaSetupComplete && Array.isArray(body.backupCodes)) {
        // Pause on a screen showing the backup codes before exiting the flow.
        setBackupCodes(body.backupCodes);
        setBusy(false);
        return;
      }
      onSuccess && onSuccess(body);
    } catch (e) {
      setErr(e?.response?.data?.message || "Invalid code");
      setBusy(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="mfa-panel">
        <h2>Save your backup codes</h2>
        <p className="mfa-sub">
          These are shown <strong>once</strong>. Each can be used in place of a
          TOTP code if you lose your authenticator. Keep them somewhere safe.
        </p>
        <pre className="mfa-codes">{backupCodes.join("\n")}</pre>
        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => {
              navigator.clipboard?.writeText(backupCodes.join("\n"));
            }}
          >
            Copy
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSuccess && onSuccess({})}
          >
            I have saved them — Continue
          </button>
        </div>
      </div>
    );
  }

  if (challenge.mfaSetup) {
    return (
      <div className="mfa-panel">
        <h2>Set up two-factor authentication</h2>
        <p className="mfa-sub">
          Open Google Authenticator, Authy, 1Password, or any other RFC 6238
          app, scan this QR code, and enter the 6-digit code shown.
        </p>
        <div className="mfa-qr">
          <QRCodeSVG value={challenge.otpauthUrl} size={196} />
        </div>
        <div className="mfa-secret-row">
          <small className="text-muted">Or enter this key manually:</small>
          <code className="mfa-secret">{challenge.secret}</code>
        </div>
        <form onSubmit={submit} className="mfa-form mt-3">
          <input
            ref={inputRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            className="form-control mfa-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
            disabled={busy}
          />
          {err && <div className="text-danger small mt-1">{err}</div>}
          <div className="d-flex gap-2 mt-3">
            <button type="button" className="btn btn-light" onClick={onCancel}>
              ← Back
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || code.length < 6}
            >
              {busy ? <Spinner size="sm" animation="border" /> : "Verify & enable"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // verify mode
  return (
    <div className="mfa-panel">
      <h2>Two-factor authentication</h2>
      <p className="mfa-sub">
        {useBackup
          ? "Enter one of your backup codes."
          : "Enter the 6-digit code from your authenticator app."}
      </p>
      <form onSubmit={submit} className="mfa-form">
        <input
          ref={inputRef}
          inputMode={useBackup ? "text" : "numeric"}
          autoComplete="one-time-code"
          placeholder={useBackup ? "XXXXX-XXXXX" : "6-digit code"}
          className="form-control mfa-input"
          value={code}
          onChange={(e) =>
            setCode(
              useBackup
                ? e.target.value.toUpperCase().replace(/[^A-F0-9\-]/g, "").slice(0, 11)
                : e.target.value.replace(/[^\d]/g, "").slice(0, 8)
            )
          }
          disabled={busy}
        />
        {err && <div className="text-danger small mt-1">{err}</div>}
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            ← Back
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (useBackup ? code.length < 10 : code.length < 6)}
          >
            {busy ? <Spinner size="sm" animation="border" /> : "Verify"}
          </button>
        </div>
        <button
          type="button"
          className="btn btn-link p-0 mt-2"
          onClick={() => {
            setUseBackup((u) => !u);
            setCode("");
            setErr(null);
          }}
        >
          {useBackup ? "← Use authenticator code instead" : "Use a backup code instead"}
        </button>
      </form>
    </div>
  );
}
